<template>
    <div class="grid overflow-hidden" :style="{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }">

        <div v-for="(shape, index) in shapes" :key="index" class="aspect-square relative overflow-hidden transition-all duration-500" :class="shape.class">
            <div v-if="shape.hasImage" class="absolute inset-0 bg-cover bg-center bg-no-repeat image-fade-in"
                :style="shape.imageStyle"></div>
        </div>
    </div>

</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
    variant: {
        type: String,
        required: false,
        default: 'default',
        validator: (value) => ['default', 'transparent', 'outline'].includes(value)
    },
    isAnimated: {
        type: String,
        required: false
    },
    columns: {
        type: Number,
        required: false,
        default: 6
    },
    rows: {
        type: Number,
        required: false,
        default: 4
    },
    outlineClass: {
        type: String,
        required: false,
        default: 'border-primary-500'
    },
    seed: {
        type: Number,
        required: false,
        default: null
    }
})

const totalShapes = computed(() => props.columns * props.rows);
const colourArray = ["bg-gray-900", "bg-primary-500", "bg-accent-500"];

const colourMap = ref([
    0, 1, 1, 1, 0, 0, 1, 0, 1, 1,
    1, 2, 0, 2, 1, 1, 0, 1, 2, 0,
    2, 0, 2, 1, 0, 2, 2, 2, 0, 2,
    2, 2, 0, 1, 2, 0, 2, 2, 2, 0,
    0, 1, 1, 2, 0, 2, 1, 0, 1, 1,
    1, 0, 0, 1, 1, 1, 0, 1, 0, 0
])

const shapeMap = ref(Array.from({ length: colourMap.value.length }, () => Math.floor(Math.random() * 10) + 1));

const shapes = computed(() => {
    var result = [];

    var colourClass = '';
    if (props.variant === 'transparent') {
        colourClass = 'bg-white/10';
    } else if (props.variant === 'outline') {
        colourClass = `border ${props.outlineClass}`;
    }

    for (var x = 0; x < totalShapes.value; x++) {

        if(props.variant === 'default') {
            colourClass = colourArray[colourMap.value[x % colourMap.value.length]];
        }

        result.push({
            class: `entegy-shape-${shapeMap.value[x % shapeMap.value.length]} ${colourClass}`
        })
    }
    return result;
})

// Fisher-Yates shuffle algorithm
function shuffleArray(array) {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
}

function shuffleMaps() {
    colourMap.value = shuffleArray(colourMap.value);
    shapeMap.value = shuffleArray(shapeMap.value);
}

let intervalId = null;

onMounted(() => {
    if (props.isAnimated) {
        // Shuffle every 700ms (or adjust interval as needed)
        intervalId = setInterval(shuffleMaps, 2000);
    }
});

onUnmounted(() => {
    if (intervalId) {
        clearInterval(intervalId);
    }
});

// Watch for changes to isAnimated prop
watch(() => props.isAnimated, (newValue) => {
    if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
    }
    
    if (newValue) {
        intervalId = setInterval(shuffleMaps, 2000);
    }
});

watch(() => props.seed, (newValue) => {
    if (newValue) {
        shuffleMaps();
    }
});


</script>